import React, { useContext, useEffect, useRef, useState } from 'react';
import shortid from 'shortid';
import { has, is } from '../../../services';
import clsx from 'clsx';
import { hashClass } from '../../../helpers';
import { useTrilogyContext } from '../../../context';
import { AccordionContext } from '../Accordion';
/**
 * Accordion Item Component
 * @param active {boolean} Active Accordion Item
 * @param className {string} Additionnal CSS Classes
 * @param id {string} id for accordion item
 * @param onClick {ClickEvent} onClick Event
 * @param disabled {boolean} Disabled AccordionItem
 * @param children {ReactNode}
 * @param onOpen {Function} onOpen Accordion Item Function
 * @param onClose {Function} onClose Accordion Item Function
 * @param paddingless {boolean} Remove padding for Accordion Item
 * ================= NATIVE =================
 * @param headerItems {ReactNode} Header Items
 * @param bodyItems {ReactNode} Body Items
 * @param bordered {boolean} Accordion Item Bordered
 * @param boxed {boolean} Boxed Accordion Item
 */
const AccordionItem = ({ active, className, children, id = shortid.generate(), onClick, disabled, onClose, onOpen, paddingless, inverted, ...others }) => {
    const ref = useRef(null);
    const { styled } = useTrilogyContext();
    const accordionContextValues = useContext(AccordionContext);
    const [isActive, setIsActive] = useState(active || false);
    const [expandedHeight, setExpandedHeight] = useState();
    const [collapsedHeight, setCollapsedHeight] = useState();
    // Faire à l'avance un pré-calcul de la hauteur de l'accordéon plié et déplié,
    // Ces infos sont enregistrées dans les data-attributs "data-collapsed" et "data-expanded".
    // Nécessaire quand l'accordion est consommé par un parent où un traitement supplémentaire
    // est requis comme un scrollTo par exemple. Dans ce cas là on transmet au component react
    // un événement onClick qui initie le scrollTo en amont de déclencher le toggle.
    // C'est-à-dire avant que l'accordion a été rendu dans son état déplié.
    useEffect(() => {
        const e = ref.current;
        if (!e) {
            return;
        }
        const { floor, abs } = Math;
        const firstChild = e.children[1].firstChild;
        const expandedInactive = floor(abs(e.clientHeight + (firstChild === null || firstChild === void 0 ? void 0 : firstChild.clientHeight))).toString();
        const expandedActive = floor(abs(e.clientHeight)).toString();
        const collapsedInactive = floor(abs(e.clientHeight + 1)).toString();
        const collapsedActive = floor(abs(e.children[0].clientHeight)).toString();
        setExpandedHeight(isActive ? expandedActive : expandedInactive);
        setCollapsedHeight(isActive ? collapsedActive : collapsedInactive);
    }, [isActive]);
    useEffect(() => {
        setIsActive(active || false);
    }, [active]);
    const classes = hashClass(styled, clsx('accordion', className, isActive && is('active'), paddingless && is('paddingless'), disabled && is('disabled'), (inverted || accordionContextValues.inverted) && has('background-white')));
    let childrenElement = null;
    if (children) {
        childrenElement = Array.isArray(children)
            ? children.map((child, index) => {
                return React.cloneElement(child, {
                    key: `article-${index}`,
                    dataId: index === 0 ? `header-${id}` : `body-${id}`,
                    active,
                    disabled,
                });
            })
            : children;
    }
    return (React.createElement("article", { "data-testid": id, className: classes, ref: ref, id: id, ...others, "data-collapsed": collapsedHeight, "data-expanded": expandedHeight, onClick: (e) => {
            if (onOpen && !isActive)
                onOpen(e);
            if (onClose && isActive)
                onClose(e);
            if (onClick)
                onClick(e);
        } },
        !disabled && (React.createElement("input", { type: 'checkbox', "data-accordion-toggle": true, id: `toggle_header-${id}`, style: { display: 'none' }, onChange: () => setIsActive(isActive => !isActive), checked: isActive, autoComplete: 'off' })),
        childrenElement));
};
export default AccordionItem;
//# sourceMappingURL=AccordionItem.js.map